import React, { useState, useEffect } from "react";
import { CreditCard, Lock } from "lucide-react";
import api, { formatBRL } from "../lib/api";
import { createMpCardToken, preloadMp } from "../lib/mpClient";

/**
 * V9.0 — Formulário de cartão Asaas (tokenização server-side, sem redirect).
 * Coleta os dados do cartão + CEP/número (exigidos pela Asaas) e devolve via onSubmit.
 * O componente NÃO faz chamadas — quem chama trata a API e os erros.
 */
function onlyDigits(s) {
  return (s || "").replace(/\D/g, "");
}

function formatCardNumber(v) {
  const d = onlyDigits(v).slice(0, 19);
  return d.replace(/(.{4})/g, "$1 ").trim();
}

function formatExpiry(v) {
  const d = onlyDigits(v).slice(0, 4);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}/${d.slice(2)}`;
}

export const AsaasCardForm = ({ amount, submitting, onSubmit, submitLabel, showInstallments = false, holderCpf = "" }) => {
  const [holder, setHolder] = useState("");
  const [number, setNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [ccv, setCcv] = useState("");
  const [localError, setLocalError] = useState("");
  const [instOptions, setInstOptions] = useState([]);
  const [installments, setInstallments] = useState(1);

  // V13.8: pré-carrega o SDK do MP ao abrir o form (gera o device session id p/ anti-fraude).
  useEffect(() => { preloadMp(); }, []);

  useEffect(() => {
    if (!showInstallments || !amount) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/payments/installments", { params: { amount } });
        if (active) setInstOptions(data.options || []);
      } catch { if (active) setInstOptions([]); }
    })();
    return () => { active = false; };
  }, [showInstallments, amount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");
    const num = onlyDigits(number);
    const exp = onlyDigits(expiry);
    if (!holder.trim()) return setLocalError("Informe o nome impresso no cartão.");
    if (num.length < 13) return setLocalError("Número do cartão inválido.");
    if (exp.length !== 4) return setLocalError("Validade inválida (MM/AA).");
    if (onlyDigits(ccv).length < 3) return setLocalError("CVV inválido.");

    const mm = exp.slice(0, 2);
    let yy = exp.slice(2);
    yy = yy.length === 2 ? `20${yy}` : yy;

    // V12.9: gera token MP em paralelo (silencioso) p/ fallback automático se a Asaas falhar
    const mpExtra = await createMpCardToken({
      number: num, holder: holder.trim(), mm, yy, ccv: onlyDigits(ccv), cpf: holderCpf,
    });

    onSubmit({
      card_holder_name: holder.trim(),
      card_number: num,
      card_expiry_month: mm,
      card_expiry_year: yy,
      card_ccv: onlyDigits(ccv),
      installments: showInstallments ? installments : 1,
      ...mpExtra,
    });
  };

  const selectedOpt = instOptions.find((o) => o.installments === installments);
  const payAmount = showInstallments && selectedOpt ? selectedOpt.total : amount;

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="asaas-card-form">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Lock className="w-3.5 h-3.5" />
        <span>Pagamento seguro e criptografado. O valor fica reservado e só é cobrado na retirada.</span>
      </div>

      {showInstallments && instOptions.length > 0 && (
        <div data-testid="card-installments-wrap">
          <label className="text-xs font-semibold text-slate-600">Parcelas</label>
          <select
            className="aj-input mt-1" value={installments}
            onChange={(e) => setInstallments(Number(e.target.value))}
            data-testid="card-installments-select"
          >
            {instOptions.map((o) => (
              <option key={o.installments} value={o.installments}>
                {o.installments}x de {formatBRL(o.installment_value)}
                {o.interest_free ? " (sem juros)" : ` — total ${formatBRL(o.total)}`}
              </option>
            ))}
          </select>
          {selectedOpt && !selectedOpt.interest_free && (
            <div className="text-[10px] text-slate-500 mt-1" data-testid="card-installments-note">
              Juros de parcelamento: +{formatBRL(selectedOpt.surcharge)} (total {formatBRL(selectedOpt.total)})
            </div>
          )}
        </div>
      )}

      <div>
        <label className="text-xs font-semibold text-slate-600">Nome no cartão</label>
        <input
          className="aj-input mt-1" placeholder="Como está no cartão"
          autoComplete="cc-name"
          value={holder} onChange={(e) => setHolder(e.target.value.toUpperCase())}
          data-testid="card-holder-name"
        />
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-600">Número do cartão</label>
        <div className="relative">
          <CreditCard className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="aj-input mt-1 pl-9" placeholder="0000 0000 0000 0000" inputMode="numeric"
            autoComplete="cc-number" name="cardnumber"
            value={formatCardNumber(number)}
            onChange={(e) => setNumber(onlyDigits(e.target.value).slice(0, 19))}
            onPaste={(e) => {
              e.preventDefault();
              const txt = ((e.clipboardData || window.clipboardData)?.getData("text")) || "";
              setNumber(onlyDigits(txt).slice(0, 19));
            }}
            data-testid="card-number"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-600">Validade</label>
          <input
            className="aj-input mt-1" placeholder="MM/AA" inputMode="numeric"
            autoComplete="cc-exp"
            value={formatExpiry(expiry)} onChange={(e) => setExpiry(e.target.value)}
            data-testid="card-expiry"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">CVV</label>
          <input
            className="aj-input mt-1" placeholder="123" inputMode="numeric" maxLength={4}
            autoComplete="cc-csc"
            value={onlyDigits(ccv)} onChange={(e) => setCcv(e.target.value)}
            data-testid="card-ccv"
          />
        </div>
      </div>

      {localError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-xs text-red-700" data-testid="card-local-error">
          {localError}
        </div>
      )}

      <button
        type="submit" disabled={submitting}
        className="w-full bg-[#004E98] text-white font-bold py-3 rounded-xl disabled:opacity-60 transition-opacity"
        data-testid="card-submit-btn"
      >
        {submitting ? "Processando..." : (submitLabel || (payAmount ? `Pagar ${formatBRL(payAmount)}` : "Pagar"))}
      </button>
    </form>
  );
};

export default AsaasCardForm;
